/*
 * Poly2Tri Copyright (c) 2009-2014, Poly2Tri Contributors
 * http://code.google.com/p/poly2tri/
 *
 * poly2tri.js (JavaScript port) (c) 2009-2014, Poly2Tri Contributors
 * https://github.com/r3mi/poly2tri.js
 *
 * All rights reserved.
 *
 * Distributed under the 3-clause BSD License, see LICENSE.txt
 */

import { assert } from './assert'
import { PointError } from './pointerror'
import { Triangle } from './triangle'
import { Node } from './advancingfront'
import { EPSILON, Orientation, orient2d, inScanArea, isAngleObtuse } from './utils'
import type { XY } from './xy'
import type { SweepContext, Edge } from './sweepcontext'

/**
 * Triangulate the polygon with holes and Steiner points.
 * Do this AFTER you've added the polyline, holes, and Steiner points
 */
export function triangulate(tcx: SweepContext): void {
	tcx.initTriangulation()
	tcx.createAdvancingFront()
	// Sweep points; build mesh
	sweepPoints(tcx)
	// Clean up
	finalizationPolygon(tcx)
}

/**
 * Start sweeping the Y-sorted point set from bottom to top
 */
function sweepPoints(tcx: SweepContext): void {
	const len = tcx.pointCount()
	for (let i = 1; i < len; ++i) {
		const point = tcx.getPoint(i)
		const node = pointEvent(tcx, point)
		const edges = point._p2t_edge_list
		if (edges) {
			for (const edge of edges) {
				edgeEventByEdge(tcx, edge, node)
			}
		}
	}
}

function finalizationPolygon(tcx: SweepContext): void {
	// Get an Internal triangle to start with
	let t = tcx.front()!.head().next!.triangle!
	const p = tcx.front()!.head().next!.point
	while (!t.getConstrainedEdgeCW(p)) {
		t = t.neighborCCW(p)!
	}

	// Collect interior triangles constrained by edges
	tcx.meshClean(t)
}

/**
 * Find closest node to the left of the new point and
 * create a new triangle. If needed new holes and basins
 * will be filled too.
 */
function pointEvent(tcx: SweepContext, point: XY): Node {
	const node = tcx.locateNode(point)!
	const new_node = newFrontTriangle(tcx, point, node)

	// Only need to check +epsilon since point never have smaller
	// x value than node due to how we fetch nodes from the front
	if (point.x <= node.point.x + EPSILON) {
		fill(tcx, node)
	}

	fillAdvancingFront(tcx, new_node)
	return new_node
}

function edgeEventByEdge(tcx: SweepContext, edge: Edge, node: Node): void {
	tcx.edge_event.constrained_edge = edge
	tcx.edge_event.right = edge.p.x > edge.q.x

	if (isEdgeSideOfTriangle(node.triangle!, edge.p, edge.q)) {
		return
	}

	// For now we will do all needed filling
	// TODO: integrate with flip process might give some better performance
	//       but for now this avoid the issue with cases that needs both flips and fills
	fillEdgeEvent(tcx, edge, node)
	edgeEventByPoints(tcx, edge.p, edge.q, node.triangle!, edge.q)
}

function edgeEventByPoints(tcx: SweepContext, ep: XY, eq: XY, triangle: Triangle, point: XY): void {
	if (isEdgeSideOfTriangle(triangle, ep, eq)) {
		return
	}

	const p1 = triangle.pointCCW(point)!
	const o1 = orient2d(eq, p1, ep)
	if (o1 === Orientation.COLLINEAR) {
		throw new PointError('poly2tri EdgeEvent: Collinear not supported!', [eq, p1, ep])
	}

	const p2 = triangle.pointCW(point)!
	const o2 = orient2d(eq, p2, ep)
	if (o2 === Orientation.COLLINEAR) {
		throw new PointError('poly2tri EdgeEvent: Collinear not supported!', [eq, p2, ep])
	}

	if (o1 === o2) {
		// Need to decide if we are rotating CW or CCW to get to a triangle
		// that will cross edge
		if (o1 === Orientation.CW) {
			triangle = triangle.neighborCCW(point)!
		} else {
			triangle = triangle.neighborCW(point)!
		}
		edgeEventByPoints(tcx, ep, eq, triangle, point)
	} else {
		// This triangle crosses constraint so lets flippin start!
		flipEdgeEvent(tcx, ep, eq, triangle, point)
	}
}

function isEdgeSideOfTriangle(triangle: Triangle, ep: XY, eq: XY): boolean {
	const index = triangle.edgeIndex(ep, eq)
	if (index !== -1) {
		triangle.markConstrainedEdgeByIndex(index)
		const t = triangle.getNeighbor(index)
		if (t) {
			t.markConstrainedEdgeByPoints(ep, eq)
		}
		return true
	}
	return false
}

/**
 * Creates a new front triangle and legalize it
 */
function newFrontTriangle(tcx: SweepContext, point: XY, node: Node): Node {
	const triangle = new Triangle(point, node.point, node.next!.point)

	triangle.markNeighbor(node.triangle!)
	tcx.addToMap(triangle)

	const new_node = new Node(point)
	new_node.next = node.next
	new_node.prev = node
	node.next!.prev = new_node
	node.next = new_node

	if (!legalize(tcx, triangle)) {
		tcx.mapTriangleToNodes(triangle)
	}

	return new_node
}

/**
 * Adds a triangle to the advancing front to fill a hole.
 */
function fill(tcx: SweepContext, node: Node): void {
	const triangle = new Triangle(node.prev!.point, node.point, node.next!.point)

	// TODO: should copy the constrained_edge value from neighbor triangles
	//       for now constrained_edge values are copied during the legalize
	triangle.markNeighbor(node.prev!.triangle!)
	triangle.markNeighbor(node.triangle!)

	tcx.addToMap(triangle)

	// Update the advancing front
	node.prev!.next = node.next
	node.next!.prev = node.prev

	// If it was legalized the triangle has already been mapped
	if (!legalize(tcx, triangle)) {
		tcx.mapTriangleToNodes(triangle)
	}
}

/**
 * Fills holes in the Advancing Front
 */
function fillAdvancingFront(tcx: SweepContext, n: Node): void {
	// Fill right holes
	let node: Node | null = n.next
	while (node && node.next) {
		if (isAngleObtuse(node.point, node.next.point, node.prev!.point)) {
			break
		}
		fill(tcx, node)
		node = node.next
	}

	// Fill left holes
	node = n.prev
	while (node && node.prev) {
		if (isAngleObtuse(node.point, node.next!.point, node.prev.point)) {
			break
		}
		fill(tcx, node)
		node = node.prev
	}

	// Fill right basins
	if (n.next && n.next.next) {
		if (isBasinAngleRight(n)) {
			fillBasin(tcx, n)
		}
	}
}

/**
 * The basin angle is decided against the horizontal line [1,0].
 */
function isBasinAngleRight(node: Node): boolean {
	const ax = node.point.x - node.next!.next!.point.x
	const ay = node.point.y - node.next!.next!.point.y
	assert(ay >= 0, 'unordered y')
	return ax >= 0 || Math.abs(ax) < ay
}

/**
 * Returns true if triangle was legalized
 */
function legalize(tcx: SweepContext, t: Triangle): boolean {
	// To legalize a triangle we start by finding if any of the three edges
	// violate the Delaunay condition
	for (let i = 0; i < 3; ++i) {
		if (t.delaunay_edge[i]) {
			continue
		}
		const ot = t.getNeighbor(i)
		if (ot) {
			const p = t.getPoint(i)
			const op = ot.oppositePoint(t, p)!
			const oi = ot.index(op)

			// If this is a Constrained Edge or a Delaunay Edge(only during recursive legalization)
			// then we should not try to legalize
			if (ot.constrained_edge[oi] || ot.delaunay_edge[oi]) {
				t.constrained_edge[i] = ot.constrained_edge[oi]
				continue
			}

			const inside = inCircle(p, t.pointCCW(p)!, t.pointCW(p)!, op)
			if (inside) {
				// Lets mark this shared edge as Delaunay
				t.delaunay_edge[i] = true
				ot.delaunay_edge[oi] = true

				// Lets rotate shared edge one vertex CW to legalize it
				rotateTrianglePair(t, p, ot, op)

				// We now got one valid Delaunay Edge shared by two triangles
				// This gives us 4 new edges to check for Delaunay

				// Make sure that triangle to node mapping is done only one time for a specific triangle
				let not_legalized = !legalize(tcx, t)
				if (not_legalized) {
					tcx.mapTriangleToNodes(t)
				}

				not_legalized = !legalize(tcx, ot)
				if (not_legalized) {
					tcx.mapTriangleToNodes(ot)
				}
				// Reset the Delaunay edges, since they only are valid Delaunay edges
				// until we add a new triangle or point.
				// XXX: need to think about this. Can these edges be tried after we
				//      return to previous recursive level?
				t.delaunay_edge[i] = false
				ot.delaunay_edge[oi] = false

				// If triangle have been legalized no need to check the other edges since
				// the recursive legalization will handles those so we can end here.
				return true
			}
		}
	}
	return false
}

/**
 * Check if point is inside circle formed by three points.
 * Uses robust geometric predicates.
 */
function inCircle(pa: XY, pb: XY, pc: XY, pd: XY): boolean {
	const adx = pa.x - pd.x
	const ady = pa.y - pd.y
	const bdx = pb.x - pd.x
	const bdy = pb.y - pd.y

	const adxbdy = adx * bdy
	const bdxady = bdx * ady
	const oabd = adxbdy - bdxady
	if (oabd <= 0) {
		return false
	}

	const cdx = pc.x - pd.x
	const cdy = pc.y - pd.y

	const cdxady = cdx * ady
	const adxcdy = adx * cdy
	const ocad = cdxady - adxcdy
	if (ocad <= 0) {
		return false
	}

	const bdxcdy = bdx * cdy
	const cdxbdy = cdx * bdy

	const alift = adx * adx + ady * ady
	const blift = bdx * bdx + bdy * bdy
	const clift = cdx * cdx + cdy * cdy

	const det = alift * (bdxcdy - cdxbdy) + blift * ocad + clift * oabd
	return det > 0
}

/**
 * Rotates a triangle pair one vertex CW
 */
function rotateTrianglePair(t: Triangle, p: XY, ot: Triangle, op: XY): void {
	const n1 = t.neighborCCW(p)
	const n2 = t.neighborCW(p)
	const n3 = ot.neighborCCW(op)
	const n4 = ot.neighborCW(op)

	const ce1 = t.getConstrainedEdgeCCW(p)
	const ce2 = t.getConstrainedEdgeCW(p)
	const ce3 = ot.getConstrainedEdgeCCW(op)
	const ce4 = ot.getConstrainedEdgeCW(op)

	const de1 = t.getDelaunayEdgeCCW(p)
	const de2 = t.getDelaunayEdgeCW(p)
	const de3 = ot.getDelaunayEdgeCCW(op)
	const de4 = ot.getDelaunayEdgeCW(op)

	t.legalize(p, op)
	ot.legalize(op, p)

	// Remap delaunay_edge
	ot.setDelaunayEdgeCCW(p, de1)
	t.setDelaunayEdgeCW(p, de2)
	t.setDelaunayEdgeCCW(op, de3)
	ot.setDelaunayEdgeCW(op, de4)

	// Remap constrained_edge
	ot.setConstrainedEdgeCCW(p, ce1)
	t.setConstrainedEdgeCW(p, ce2)
	t.setConstrainedEdgeCCW(op, ce3)
	ot.setConstrainedEdgeCW(op, ce4)

	// Remap neighbors
	// XXX: might optimize the markNeighbor by keeping track of
	//      what side should be assigned to what neighbor after the
	//      rotation. Now mark neighbor does lots of testing to find
	//      the right side.
	t.clearNeighbors()
	ot.clearNeighbors()
	if (n1) ot.markNeighbor(n1)
	if (n2) t.markNeighbor(n2)
	if (n3) t.markNeighbor(n3)
	if (n4) ot.markNeighbor(n4)
	t.markNeighbor(ot)
}

/**
 * Fills a basin that has formed on the Advancing Front to the right
 * of given node.
 */
function fillBasin(tcx: SweepContext, node: Node): void {
	if (orient2d(node.point, node.next!.point, node.next!.next!.point) === Orientation.CCW) {
		tcx.basin.left_node = node.next!.next
	} else {
		tcx.basin.left_node = node.next
	}

	// Find the bottom and right node
	tcx.basin.bottom_node = tcx.basin.left_node
	while (tcx.basin.bottom_node!.next &&
	       tcx.basin.bottom_node!.point.y >= tcx.basin.bottom_node!.next.point.y) {
		tcx.basin.bottom_node = tcx.basin.bottom_node!.next
	}
	if (tcx.basin.bottom_node === tcx.basin.left_node) {
		// No valid basin
		return
	}

	tcx.basin.right_node = tcx.basin.bottom_node
	while (tcx.basin.right_node!.next &&
	       tcx.basin.right_node!.point.y < tcx.basin.right_node!.next.point.y) {
		tcx.basin.right_node = tcx.basin.right_node!.next
	}
	if (tcx.basin.right_node === tcx.basin.bottom_node) {
		// No valid basins
		return
	}

	tcx.basin.width = tcx.basin.right_node!.point.x - tcx.basin.left_node!.point.x
	tcx.basin.left_highest = tcx.basin.left_node!.point.y > tcx.basin.right_node!.point.y

	if (!tcx.basin.bottom_node) {
		return
	}

	fillBasinReq(tcx, tcx.basin.bottom_node)
}

/**
 * Recursive algorithm to fill a Basin with triangles
 */
function fillBasinReq(tcx: SweepContext, node: Node): void {
	// if shallow stop filling
	if (isShallow(tcx, node)) {
		return
	}

	fill(tcx, node)

	let o: Orientation
	if (node.prev === tcx.basin.left_node && node.next === tcx.basin.right_node) {
		return
	} else if (node.prev === tcx.basin.left_node) {
		o = orient2d(node.point, node.next!.point, node.next!.next!.point)
		if (o === Orientation.CW) {
			return
		}
		node = node.next!
	} else if (node.next === tcx.basin.right_node) {
		o = orient2d(node.point, node.prev!.point, node.prev!.prev!.point)
		if (o === Orientation.CCW) {
			return
		}
		node = node.prev!
	} else {
		// Continue with the neighbor node with lowest Y value
		if (node.prev!.point.y < node.next!.point.y) {
			node = node.prev!
		} else {
			node = node.next!
		}
	}

	fillBasinReq(tcx, node)
}

function isShallow(tcx: SweepContext, node: Node): boolean {
	let height: number
	if (tcx.basin.left_highest) {
		height = tcx.basin.left_node!.point.y - node.point.y
	} else {
		height = tcx.basin.right_node!.point.y - node.point.y
	}

	// if shallow stop filling
	if (tcx.basin.width > height) {
		return true
	}
	return false
}

function fillEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	if (tcx.edge_event.right) {
		fillRightAboveEdgeEvent(tcx, edge, node)
	} else {
		fillLeftAboveEdgeEvent(tcx, edge, node)
	}
}

function fillRightAboveEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	while (node.next!.point.x < edge.p.x) {
		// Check if next node is below the edge
		if (orient2d(edge.q, node.next!.point, edge.p) === Orientation.CCW) {
			fillRightBelowEdgeEvent(tcx, edge, node)
		} else {
			node = node.next!
		}
	}
}

function fillRightBelowEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	if (node.point.x < edge.p.x) {
		if (orient2d(node.point, node.next!.point, node.next!.next!.point) === Orientation.CCW) {
			// Concave
			fillRightConcaveEdgeEvent(tcx, edge, node)
		} else {
			// Convex
			fillRightConvexEdgeEvent(tcx, edge, node)
			// Retry this one
			fillRightBelowEdgeEvent(tcx, edge, node)
		}
	}
}

function fillRightConcaveEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	fill(tcx, node.next!)
	if (node.next!.point !== edge.p) {
		// Next above or below edge?
		if (orient2d(edge.q, node.next!.point, edge.p) === Orientation.CCW) {
			// Below
			if (orient2d(node.point, node.next!.point, node.next!.next!.point) === Orientation.CCW) {
				// Next is concave
				fillRightConcaveEdgeEvent(tcx, edge, node)
			}
		}
	}
}

function fillRightConvexEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	// Next concave or convex?
	if (orient2d(node.next!.point, node.next!.next!.point, node.next!.next!.next!.point) === Orientation.CCW) {
		// Concave
		fillRightConcaveEdgeEvent(tcx, edge, node.next!)
	} else {
		// Convex
		// Next above or below edge?
		if (orient2d(edge.q, node.next!.next!.point, edge.p) === Orientation.CCW) {
			// Below
			fillRightConvexEdgeEvent(tcx, edge, node.next!)
		}
	}
}

function fillLeftAboveEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	while (node.prev!.point.x > edge.p.x) {
		// Check if next node is below the edge
		if (orient2d(edge.q, node.prev!.point, edge.p) === Orientation.CW) {
			fillLeftBelowEdgeEvent(tcx, edge, node)
		} else {
			node = node.prev!
		}
	}
}

function fillLeftBelowEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	if (node.point.x > edge.p.x) {
		if (orient2d(node.point, node.prev!.point, node.prev!.prev!.point) === Orientation.CW) {
			// Concave
			fillLeftConcaveEdgeEvent(tcx, edge, node)
		} else {
			// Convex
			fillLeftConvexEdgeEvent(tcx, edge, node)
			// Retry this one
			fillLeftBelowEdgeEvent(tcx, edge, node)
		}
	}
}

function fillLeftConvexEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	// Next concave or convex?
	if (orient2d(node.prev!.point, node.prev!.prev!.point, node.prev!.prev!.prev!.point) === Orientation.CW) {
		// Concave
		fillLeftConcaveEdgeEvent(tcx, edge, node.prev!)
	} else {
		// Convex
		// Next above or below edge?
		if (orient2d(edge.q, node.prev!.prev!.point, edge.p) === Orientation.CW) {
			// Below
			fillLeftConvexEdgeEvent(tcx, edge, node.prev!)
		}
	}
}

function fillLeftConcaveEdgeEvent(tcx: SweepContext, edge: Edge, node: Node): void {
	fill(tcx, node.prev!)
	if (node.prev!.point !== edge.p) {
		// Next above or below edge?
		if (orient2d(edge.q, node.prev!.point, edge.p) === Orientation.CW) {
			// Below
			if (orient2d(node.point, node.prev!.point, node.prev!.prev!.point) === Orientation.CW) {
				// Next is concave
				fillLeftConcaveEdgeEvent(tcx, edge, node)
			}
		}
	}
}

function flipEdgeEvent(tcx: SweepContext, ep: XY, eq: XY, t: Triangle, p: XY): void {
	const ot = t.neighborAcross(p)
	assert(!!ot, 'FLIP failed due to missing triangle!')

	const op = ot.oppositePoint(t, p)!

	// Additional check from Java version (see issue #88)
	if (t.getConstrainedEdgeAcross(p)) {
		const index = t.index(p)
		throw new PointError('poly2tri Intersecting Constraints', [
			p,
			op,
			t.getPoint((index + 1) % 3),
			t.getPoint((index + 2) % 3)
		])
	}

	if (inScanArea(p, t.pointCCW(p)!, t.pointCW(p)!, op)) {
		// Lets rotate shared edge one vertex CW
		rotateTrianglePair(t, p, ot, op)
		tcx.mapTriangleToNodes(t)
		tcx.mapTriangleToNodes(ot)

		if (p === eq && op === ep) {
			if (eq === tcx.edge_event.constrained_edge!.q && ep === tcx.edge_event.constrained_edge!.p) {
				t.markConstrainedEdgeByPoints(ep, eq)
				ot.markConstrainedEdgeByPoints(ep, eq)
				legalize(tcx, t)
				legalize(tcx, ot)
			}
		} else {
			const o = orient2d(eq, op, ep)
			t = nextFlipTriangle(tcx, o, t, ot, p, op)
			flipEdgeEvent(tcx, ep, eq, t, p)
		}
	} else {
		const newP = nextFlipPoint(ep, eq, ot, op)
		flipScanEdgeEvent(tcx, ep, eq, t, ot, newP)
		edgeEventByPoints(tcx, ep, eq, t, p)
	}
}

/**
 * After a flip we have two triangles and know that only one will still be
 * intersecting the edge. So decide which to continue with and legalize the other
 */
function nextFlipTriangle(
	tcx: SweepContext,
	o: Orientation,
	t: Triangle,
	ot: Triangle,
	p: XY,
	op: XY
): Triangle {
	let edge_index: number
	if (o === Orientation.CCW) {
		// ot is not crossing edge after flip
		edge_index = ot.edgeIndex(p, op)
		ot.delaunay_edge[edge_index] = true
		legalize(tcx, ot)
		ot.clearDelaunayEdges()
		return t
	}

	// t is not crossing edge after flip
	edge_index = t.edgeIndex(p, op)

	t.delaunay_edge[edge_index] = true
	legalize(tcx, t)
	t.clearDelaunayEdges()
	return ot
}

/**
 * When we need to traverse from one triangle to the next we need
 * the point in current triangle that is the opposite point to the next
 * triangle.
 */
function nextFlipPoint(ep: XY, eq: XY, ot: Triangle, op: XY): XY {
	const o2d = orient2d(eq, op, ep)
	if (o2d === Orientation.CW) {
		// Right
		return ot.pointCCW(op)!
	} else if (o2d === Orientation.CCW) {
		// Left
		return ot.pointCW(op)!
	} else {
		throw new PointError('poly2tri [Unsupported] nextFlipPoint: opposing point on constrained edge!', [eq, op, ep])
	}
}

/**
 * Scan part of the FlipScan algorithm
 * When a triangle pair isn't flippable we will scan for the next
 * point that is inside the flip triangle scan area. When found
 * we generate a new flipEdgeEvent
 */
function flipScanEdgeEvent(tcx: SweepContext, ep: XY, eq: XY, flip_triangle: Triangle, t: Triangle, p: XY): void {
	const ot = t.neighborAcross(p)
	assert(!!ot, 'FLIP failed due to missing triangle')

	const op = ot.oppositePoint(t, p)!

	if (inScanArea(eq, flip_triangle.pointCCW(eq)!, flip_triangle.pointCW(eq)!, op)) {
		// flip with new edge op.eq
		flipEdgeEvent(tcx, eq, op, ot, op)
	} else {
		const newP = nextFlipPoint(ep, eq, ot, op)
		flipScanEdgeEvent(tcx, ep, eq, flip_triangle, ot, newP)
	}
}

